import {ErrorBoundary} from '@sentry/react';
import {useFlagsmith} from 'feature-flags';

import {useEffect, useLayoutEffect} from 'react';
import TagManager from 'react-gtm-module';
import {Outlet, useParams} from 'react-router-dom';

import {any, isNil, isNotNil, keys} from 'ramda';
import {isFalse, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {Error as ErrorComponent} from '../../(sections)/Error/Error';
import {Loading} from '../../(sections)/Loading/Loading';
import {
  useLazyGetAllApiEnumsQuery,
  useLazyGetVehicleAuditQuery,
  useLazyGetVehicleCatalogueByMakeQuery,
} from '../../api/digitalCertificateApi';
import {languageDialogItems} from '../../consts/languageDialogItems';
import {queryParams} from '../../consts/queryParams';
import {useThemeConfig} from '../../hooks/useThemeConfig';
import {FALLBACK_LANGUAGE} from '../../i18n/fallbackLanguage';
import {i18n} from '../../i18n/i18n';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {useQueryState} from '../../utils/useQueryState';

const {workspace} = getWorkspaceFromUri();

const ONE_HOUR = 3600 * 1000;
const FLAGSMITH_URL = 'https://flags.omnetic.com/api/v1/';

export function ApplicationInitializeBoundary() {
  const params = useParams<{auditId: string}>();
  const [language] = useQueryState(queryParams.LNG);
  const {isLoading: isLoadingThemeConfig} = useThemeConfig();

  const {isFlagSmithInitialized, handleInitFlagsmith} = useFlagsmith({
    apiUrl: FLAGSMITH_URL,
    environmentID: process.env.FLAGSMITH_ENV!,
    traits: workspace ? {workspace} : undefined,
    identity: `${workspace}__${params.auditId}`,
  });

  const [
    getVehicleAudit,
    {data: vehicleAudit, isFetching: isVehicleAuditFetching, isError: isVehicleAuditErrored},
  ] = useLazyGetVehicleAuditQuery({pollingInterval: ONE_HOUR});
  const [
    getAllApiEnums,
    {data: allApiEnums, isFetching: isAllApiEnumsFetching, isError: isAllApiEnumsErrored},
  ] = useLazyGetAllApiEnumsQuery({pollingInterval: ONE_HOUR});

  const [
    getVehicleCatalogue,
    {
      data: vehicleCatalogue,
      isFetching: isVehicleCatalogueByMakeQueryFetching,
      isError: isVehicleCatalogueByMakeQueryErrored,
    },
  ] = useLazyGetVehicleCatalogueByMakeQuery({pollingInterval: ONE_HOUR});

  const isFetching = any(isTrue, [
    isVehicleAuditFetching,
    isAllApiEnumsFetching,
    isVehicleCatalogueByMakeQueryFetching,
    isLoadingThemeConfig,
  ]);

  // Without an auditId in the route there is no certificate to resolve. Requesting
  // one anyway sends `auditId: undefined` and the API answers 400 "Invalid uuid
  // value", which is reported as an application error.
  const isAuditIdMissing = isNil(params.auditId);

  const isErrored = any(isTrue, [
    isAuditIdMissing,
    isVehicleAuditErrored,
    isAllApiEnumsErrored,
    isVehicleCatalogueByMakeQueryErrored,
  ]);

  const isAppReady = isNotNil(vehicleAudit) && isNotNil(allApiEnums) && isNotNil(vehicleCatalogue);

  useLayoutEffect(() => {
    if (isSupportedLanguage(language) && language !== i18n?.resolvedLanguage) {
      i18n.changeLanguage(language!);
    }

    if (isErrored) {
      return;
    }

    if (!isAppReady) {
      getVehicleAudit({workspace, auditId: params.auditId}).then((result) => {
        if (isNil(result.data)) {
          return;
        }
        getVehicleCatalogue({
          vehicleType: result.data.vehicle.type,
          make: result.data.vehicle.make,
          lang: language ?? i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE,
        });
        getAllApiEnums({
          lang: [language ?? i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
          vehicleType: result.data.vehicle.type ? [result.data.vehicle.type] : undefined,
        });
      });
    }
  }, [
    getAllApiEnums,
    getVehicleAudit,
    getVehicleCatalogue,
    handleInitFlagsmith,
    isAppReady,
    isErrored,
    language,
    params.auditId,
  ]);

  useEffect(
    function pushInitializeDataLayer() {
      if (!isAppReady || process.env.ENV_TYPE !== 'prod') {
        return;
      }

      const dataLayer = {
        event: 'applicationInitializeBoundary',
        workspace,
        environment: process.env.ENV_TYPE,
        auditId: vehicleAudit.audit.id,
        vehicleType: vehicleAudit.vehicle.type,
        make: vehicleAudit.vehicle.make,
        language: language ?? i18n.language,
      };

      TagManager.dataLayer({dataLayer});
    },
    [isAppReady, language, vehicleAudit]
  );

  // eslint-disable-next-line no-nested-ternary -- Pre-existing violation
  const component = isErrored ? (
    <ErrorComponent />
  ) : isFetching || isFalse(isAppReady) || isFalse(isFlagSmithInitialized) ? (
    <Loading />
  ) : (
    <Outlet />
  );

  return <ErrorBoundary fallback={<ErrorComponent />}>{component}</ErrorBoundary>;
}

// eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
const isSupportedLanguage = (lng: any) =>
  isNotNilOrEmpty(lng) && keys(languageDialogItems).includes(lng);
